import { ComponentType, useEffect, useState } from 'react';

import { ChartMarker } from '@cast/design-system';

import type { CostReportChartDataPoint } from 'features/cost-report/types/charts';

import { CostReportAnomalyMarker } from './CostReportAnomalyMarker';
import {
  CostReportChart,
  CostReportChartProps,
  TickChartDataPoint,
} from './CostReportChart';
import type { AnomaliesAware } from '../../types/costOverTime';

type Props<T extends AnomaliesAware> = CostReportChartProps<T> & {
  AnomalyTooltipComponent: ComponentType<{
    payload: T;
  }>;
};

export const CostReportChartWithAnomalies = <
  T extends AnomaliesAware & CostReportChartDataPoint
>({
  AnomalyTooltipComponent,
  tooltipHeader,
  tooltipFooter,
  ...costChartProps
}: Props<T>) => {
  const [displayedAnomaly, setDisplayedAnomaly] = useState<T | null>(null);

  useEffect(() => {
    setDisplayedAnomaly(null);
  }, [costChartProps.data]);

  return (
    <CostReportChart<T>
      {...costChartProps}
      tooltipHeader={displayedAnomaly ? undefined : tooltipHeader}
      tooltipFooter={
        displayedAnomaly
          ? () => <AnomalyTooltipComponent payload={displayedAnomaly} />
          : tooltipFooter
      }
      tooltipAlwaysActive={displayedAnomaly ? true : undefined}
    >
      <ChartMarker<TickChartDataPoint<T>>
        unixDataKey="chartTimestampUnix"
        shouldRender={(payload) => payload.anomalies?.length > 0}
        shape={({ payload, cx = 0, cy = 0, index = 0 }) => (
          <CostReportAnomalyMarker
            cx={cx}
            cy={cy}
            index={index}
            anomaliesAmount={payload.anomalies.length}
            onDisplayDetails={(display) =>
              setDisplayedAnomaly(display ? payload : null)
            }
          />
        )}
      />
    </CostReportChart>
  );
};
